// GA4 gtag debugging utilities
export interface GtagDebugInfo {
  isGtagAvailable: boolean;
  isDataLayerAvailable: boolean;
  scriptLoaded: boolean;
  consentMode: any;
  errors: string[];
}

// Check if gtag is properly loaded and available
export function checkGtagAvailability(): GtagDebugInfo {
  const errors: string[] = [];
  
  // Check if window.gtag exists
  const isGtagAvailable = typeof window !== 'undefined' && typeof window.gtag === 'function';
  if (!isGtagAvailable) {
    errors.push('window.gtag is not available or not a function');
  }
  
  // Check if dataLayer exists
  const isDataLayerAvailable = typeof window !== 'undefined' && Array.isArray(window.dataLayer);
  if (!isDataLayerAvailable) {
    errors.push('window.dataLayer is not available or not an array');
  }
  
  // Check if GA4 script is loaded
  const scriptLoaded = document.querySelector('script[src*="googletagmanager.com/gtag/js"]') !== null;
  if (!scriptLoaded) {
    errors.push('GA4 script tag not found in DOM');
  }
  
  // Get current consent mode state
  let consentMode = null;
  try {
    if (isGtagAvailable && isDataLayerAvailable) {
      // Try to get consent state from dataLayer
      consentMode = window.dataLayer.filter(item => 
        item && typeof item === 'object' && item[0] === 'consent'
      );
    }
  } catch (error) {
    errors.push(`Error checking consent mode: ${error}`);
  }
  
  return {
    isGtagAvailable,
    isDataLayerAvailable,
    scriptLoaded,
    consentMode,
    errors
  };
}

// Wait for gtag to be available with timeout
export function waitForGtag(timeout: number = 5000): Promise<boolean> {
  return new Promise((resolve) => {
    const startTime = Date.now();
    
    const checkGtag = () => {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        resolve(true);
        return;
      }
      
      if (Date.now() - startTime > timeout) {
        resolve(false);
        return;
      }
      
      setTimeout(checkGtag, 100);
    };
    
    checkGtag();
  });
}

// Enhanced gtag function with error handling and fallback
export function safeGtag(...args: any[]): boolean {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag(...args);
      return true;
    } else {
      console.warn('gtag not available, queuing command:', args);
      // Queue the command for when gtag becomes available
      if (typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(args);
      }
      return false;
    }
  } catch (error) {
    console.error('Error calling gtag:', error, 'Args:', args);
    return false;
  }
}

// Log detailed debug information
export function logGtagDebugInfo(): void {
  const debugInfo = checkGtagAvailability();
  
  console.group('🔍 GA4 gtag Debug Information');
  console.log('gtag available:', debugInfo.isGtagAvailable);
  console.log('dataLayer available:', debugInfo.isDataLayerAvailable);
  console.log('Script loaded:', debugInfo.scriptLoaded);
  console.log('Consent mode data:', debugInfo.consentMode);
  
  if (debugInfo.errors.length > 0) {
    console.group('❌ Errors:');
    debugInfo.errors.forEach(error => console.error(error));
    console.groupEnd();
  }
  
  // Additional debugging info
  if (typeof window !== 'undefined') {
    console.log('dataLayer contents:', window.dataLayer);
    console.log('All GA4 scripts:', document.querySelectorAll('script[src*="googletagmanager.com"]'));
  }
  
  console.groupEnd();
}

// Initialize gtag debugging in development
export function initGtagDebugging(): void {
  if (process.env.NODE_ENV === 'development') {
    // Log debug info immediately
    logGtagDebugInfo();
    
    // Wait for gtag and log again
    waitForGtag(10000).then(available => {
      console.log('🎯 gtag availability after waiting:', available);
      if (available) {
        logGtagDebugInfo();
      }
    });
    
    // Add global debug function
    if (typeof window !== 'undefined') {
      (window as any).debugGtag = logGtagDebugInfo;
      console.log('💡 Run window.debugGtag() in console for detailed debug info');
    }
  }
}
